/**
 * Semáforo y metadatos de estado del NOC (Guía §3.5 y §10).
 *
 * Regla de accesibilidad: **el color nunca es el único indicador**. Cada estado expone
 * etiqueta de texto e `iconKey`; el consumidor debe renderizar color + icono + texto.
 * El oro se reserva como acento/advertencia sobre fondos oscuros, nunca como texto sobre claro.
 */
import type {
  NocAlertSeverity,
  NocAlertStatus,
  NocAlertType,
  NocConnectorState,
  NocSemaphore,
  NocStationState,
  StationOperationStatus,
  StationRegistryStatus,
  StationStatus,
  StationSyncStatus,
} from '@goelectric/shared-types';
import { brandColors, darkColors, neutralColors, semanticColors } from '../tokens';

/** Clave de icono que el consumidor mapea a su set (antd icons). */
export type StatusIconKey =
  | 'check'
  | 'bolt'
  | 'lock'
  | 'warning'
  | 'offline'
  | 'tool'
  | 'info'
  | 'temperature';

export interface StatusMeta {
  /** Etiqueta visible (obligatoria: no solo color). */
  label: string;
  description: string;
  /** Color para tema claro (fondo de badge o texto según uso). */
  color: string;
  /** Color ajustado para tema oscuro. */
  colorDark: string;
  iconKey: StatusIconKey;
}

/** Semáforo compacto del mapa (3 estados). */
export const NOC_SEMAPHORE_META: Record<NocSemaphore, StatusMeta> = {
  VERDE: {
    label: 'Operativo',
    description: 'Estación disponible, cargando u ocupada.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  ROJO: {
    label: 'Falla',
    description: 'Estación con falla reportada.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
  GRIS: {
    label: 'Sin conexión',
    description: 'Estación offline, stale o en mantenimiento.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/** Estado granular del cargador (6 estados) con su semáforo asociado. */
export const NOC_STATION_STATE_META: Record<
  NocStationState,
  StatusMeta & { semaphore: NocSemaphore }
> = {
  AVAILABLE: {
    label: 'Disponible',
    description: 'Conector libre, listo para iniciar una carga.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
    semaphore: 'VERDE',
  },
  CHARGING: {
    label: 'Cargando',
    description: 'Sesión de carga activa en curso.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'bolt',
    semaphore: 'VERDE',
  },
  OCCUPIED: {
    label: 'Ocupado',
    description: 'Conector en uso con vehículo conectado, sin carga en curso.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'lock',
    semaphore: 'VERDE',
  },
  FAULTED: {
    label: 'Falla',
    description: 'Conector en falla o error crítico.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
    semaphore: 'ROJO',
  },
  OFFLINE: {
    label: 'Sin conexión',
    description: 'La estación no reporta heartbeat.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
    semaphore: 'GRIS',
  },
  MAINTENANCE: {
    label: 'Mantenimiento',
    description: 'Fuera de servicio de forma planificada.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'tool',
    semaphore: 'GRIS',
  },
};

/** Estado de conector (5 canónicos + UNKNOWN) para la consola de laboratorio. */
export const NOC_CONNECTOR_STATE_META: Record<NocConnectorState, StatusMeta> = {
  AVAILABLE: {
    label: 'Disponible',
    description: 'Conector libre.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  OCCUPIED: {
    label: 'Ocupado',
    description: 'Conector en uso con vehículo conectado, sin carga en curso.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'lock',
  },
  RESERVED: {
    label: 'Reservado',
    description: 'Conector reservado.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'lock',
  },
  UNAVAILABLE: {
    label: 'No disponible',
    description: 'Conector fuera de servicio.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
  FAULTED: {
    label: 'Falla',
    description: 'Conector en falla.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
  UNKNOWN: {
    label: 'Desconocido',
    description: 'Sin dato de estado.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'info',
  },
};

/** Severidades de alerta del NOC (Guía §10). */
export const NOC_ALERT_SEVERITY_META: Record<NocAlertSeverity, StatusMeta> = {
  CRITICAL: {
    label: 'Crítica',
    description: 'Equipo offline/falla: notificación persistente.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
  WARNING: {
    label: 'Advertencia',
    description: 'Mantenimiento o umbral: no bloqueante.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'tool',
  },
  INFO: {
    label: 'Informativa',
    description: 'Registro sin interrupción.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'info',
  },
};

/** Estado de gestión de una alerta. */
export const NOC_ALERT_STATUS_META: Record<NocAlertStatus, StatusMeta> = {
  OPEN: {
    label: 'Abierta',
    description: 'Requiere atención.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
  ACKED: {
    label: 'Acusada',
    description: 'Reconocida por un operador.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'check',
  },
  RESOLVED: {
    label: 'Resuelta',
    description: 'El equipo volvió a estado normal.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
};

/** Tipo de alerta (origen operativo). */
export const NOC_ALERT_TYPE_META: Record<NocAlertType, StatusMeta> = {
  FAULTED: {
    label: 'Falla de conector',
    description: 'Conector en falla.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
  CONNECTOR_UNAVAILABLE: {
    label: 'Conector fuera de servicio',
    description: 'Conector no disponible.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'offline',
  },
  STATION_OFFLINE: {
    label: 'Estación offline',
    description: 'Sin latido dentro del umbral.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'offline',
  },
  OVER_TEMPERATURE: {
    label: 'Sobretemperatura',
    description: 'Temperatura por encima del umbral.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'temperature',
  },
};

/* ------------------------------------------------------------------------- */
/* Cargadores (US-2.2) — estado de registro y de sync del core CitrineOS.     */
/* Espejo de `location-status.ts`; usa el `StatusMeta` de arriba por vivir en */
/* el mismo módulo (la interfaz es idéntica).                                 */
/* ------------------------------------------------------------------------- */

/** Estado de gestión del cargador (`AdminStationSummary.status`). */
export const STATION_STATUS_META: Record<StationStatus, StatusMeta> = {
  ACTIVE: {
    label: 'Activo',
    description: 'Cargador habilitado para operar en la red.',
    color: semanticColors.success,
    // `colorDark` debe ser la variante del tema oscuro, no el color claro repetido: el
    // verde claro (#1d7a4a) no llega a AA sobre fondos oscuros (~3:1). Mismo criterio que
    // las metas del NOC, que sí usan `darkColors`.
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  INACTIVE: {
    label: 'Dado de baja',
    description: 'Cargador deshabilitado (soft-delete).',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/** Estado de sincronización de la ChargingStation del core CitrineOS (ADR 0024). */
export const STATION_SYNC_META: Record<StationSyncStatus, StatusMeta> = {
  PENDIENTE: {
    label: 'Pendiente',
    description: 'Cargador aún no sincronizado con CitrineOS.',
    color: semanticColors.warning,
    colorDark: darkColors.accentOnDark,
    iconKey: 'info',
  },
  SYNCED: {
    label: 'Sincronizado',
    description: 'Cargador sincronizado con CitrineOS.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  ERROR: {
    label: 'Error de sincronización',
    description: 'Falló la sincronización del cargador con CitrineOS.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
};

/**
 * Estado del registro (derivado). Eje durable: describe el alta y si el core ya
 * reconoció el cargador, NO si está conectado ahora (eso lo dice la operación).
 */
export const STATION_REGISTRY_META: Record<StationRegistryStatus, StatusMeta> = {
  PRE_REGISTERED: {
    label: 'Registrado',
    description: 'Cargador registrado. A la espera de ser conectado para sincronizar.',
    color: semanticColors.warning,
    colorDark: darkColors.accentOnDark,
    iconKey: 'info',
  },
  SYNCHRONIZED: {
    label: 'Sincronizado',
    description: 'Cargador registrado y conexión sincronizada con el sistema.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  ERROR: {
    label: 'Error',
    description: 'Falló el alta del cargador en la plataforma.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
  DISABLED: {
    label: 'Inhabilitado',
    description: 'Baja administrativa: el cargador salió del inventario activo.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/** Estado operativo vivo (sólo existe si el registro está `SYNCHRONIZED`). */
export const STATION_OPERATION_META: Record<StationOperationStatus, StatusMeta> = {
  DISCONNECTED: {
    label: 'Sin conexión',
    description: 'Cargador apagado o sin conexión.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
  AVAILABLE: {
    label: 'Disponible',
    description: 'Conectado y listo para iniciar una carga.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  CHARGING: {
    label: 'En carga',
    description: 'Hay una sesión de carga activa.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'bolt',
  },
  OCCUPIED: {
    label: 'Ocupado',
    description: 'Conector en uso con vehículo conectado, sin carga en curso.',
    color: brandColors.info,
    colorDark: darkColors.info,
    iconKey: 'lock',
  },
  UNAVAILABLE: {
    label: 'No disponible',
    description: 'Conectado, pero no puede iniciar una carga.',
    color: semanticColors.warning,
    colorDark: darkColors.accentOnDark,
    iconKey: 'tool',
  },
};
